import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  conversation: {
    title: string;
    giblet_variant: string;
  };
}

interface GibletChronologicalHistoryProps {
  userId: string;
  currentVariant: string;
  expanded: boolean;
  onToggle: () => void;
}

export const GibletChronologicalHistory = ({
  userId,
  currentVariant,
  expanded,
  onToggle,
}: GibletChronologicalHistoryProps) => {
  const { toast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const MESSAGES_PER_PAGE = 50;

  useEffect(() => {
    if (expanded && userId) {
      loadMessages(true);
    }
  }, [expanded, userId, currentVariant]);

  const loadMessages = async (reset = false) => {
    setLoading(true);
    const currentPage = reset ? 0 : page;
    
    try {
      const { data, error } = await supabase
        .from('giblet_chat_messages')
        .select(`
          id,
          role,
          content,
          created_at,
          conversation:conversation_id (
            title,
            giblet_variant
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(currentPage * MESSAGES_PER_PAGE, (currentPage + 1) * MESSAGES_PER_PAGE - 1);

      if (error) throw error;

      const formattedData = (data || []).map(msg => ({
        ...msg,
        role: msg.role as 'user' | 'assistant',
        conversation: Array.isArray(msg.conversation) ? msg.conversation[0] : msg.conversation
      })) as Message[];

      if (reset) {
        setMessages(formattedData);
        setPage(0);
      } else {
        setMessages(prev => [...prev, ...formattedData]);
      }

      setHasMore((data || []).length === MESSAGES_PER_PAGE);
    } catch (error: any) {
      console.error('Error loading messages:', error);
      toast({
        title: "Failed to load messages",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMore = () => {
    setPage(prev => prev + 1);
    loadMessages(false);
  };

  const filteredMessages = messages.filter(msg =>
    msg.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
    msg.conversation?.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupMessagesByDate = (msgs: Message[]) => {
    const groups: { [key: string]: Message[] } = {};
    
    msgs.forEach(msg => {
      const date = format(new Date(msg.created_at), 'MMMM d, yyyy');
      if (!groups[date]) groups[date] = [];
      groups[date].push(msg);
    });

    return groups;
  };

  const grouped = groupMessagesByDate(filteredMessages);

  if (!expanded) {
    return (
      <Card className="p-3 mb-4">
        <Button
          variant="ghost"
          onClick={onToggle}
          className="w-full justify-between"
        >
          <span className="font-medium">View Full Chronological History</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </Card>
    );
  }

  return (
    <Card className="p-4 mb-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Chronological Message History</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search all messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        <ScrollArea className="h-[400px]">
          <div className="space-y-6 pr-4">
            {loading && messages.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                Loading messages...
              </div>
            ) : Object.keys(grouped).length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-8">
                {searchQuery ? 'No messages found' : 'No messages yet'}
              </div>
            ) : (
              <>
                {Object.entries(grouped).map(([date, msgs]) => (
                  <div key={date}>
                    <h4 className="text-xs font-semibold text-muted-foreground mb-3 sticky top-0 bg-background py-1">
                      {date}
                    </h4>
                    <div className="space-y-3">
                      {msgs.map((msg) => (
                        <div
                          key={msg.id}
                          className={cn(
                            "p-3 rounded-lg border",
                            msg.role === 'user' 
                              ? "bg-primary/5 border-primary/20" 
                              : "bg-accent/50 border-accent"
                          )}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={cn(
                                "text-xs font-medium px-2 py-0.5 rounded",
                                msg.role === 'user' 
                                  ? "bg-primary/20 text-primary" 
                                  : "bg-accent text-accent-foreground"
                              )}>
                                {msg.role === 'user' ? 'You' : 'Giblet'}
                              </span>
                              {msg.conversation?.title && (
                                <span className="text-xs text-muted-foreground">
                                  {msg.conversation.title}
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(msg.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {hasMore && (
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </Button>
                )}
              </>
            )}
          </div>
        </ScrollArea>
      </div>
    </Card>
  );
};
